import { useEffect, useRef, useState } from "react";

export function useEosBackdrop() {
  const spotlightRef = useRef<HTMLDivElement | null>(null);
  const floorRef = useRef<HTMLDivElement | null>(null);
  // Touch devices generally have less headroom for scroll-linked JS and
  // continuous background-position animation running alongside native
  // scrolling, so the decorative motion is skipped there to keep scrolling
  // smooth; desktop keeps the full effect.
  const [isCoarsePointer] = useState(() => window.matchMedia("(pointer: coarse)").matches);

  useEffect(() => {
    if (isCoarsePointer) return;

    let mouseFrame: number | null = null;
    let scrollFrame: number | null = null;

    const handleMouseMove = (e: MouseEvent) => {
      if (mouseFrame) return;
      mouseFrame = requestAnimationFrame(() => {
        const xPct = (e.clientX / window.innerWidth) * 100;
        const yPct = (e.clientY / window.innerHeight) * 100;
        spotlightRef.current?.style.setProperty("--mx", `${xPct}%`);
        spotlightRef.current?.style.setProperty("--my", `${yPct}%`);
        mouseFrame = null;
      });
    };

    // The floor stays vertically pinned (position: fixed); scrolling only spins it
    // around its vertical axis, so scrolling reads as forward motion, not panning.
    const handleScroll = () => {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(() => {
        const spinDeg = (window.scrollY * 0.06) % 360;
        if (floorRef.current) {
          floorRef.current.style.transform = `rotateX(78deg) rotateZ(${spinDeg}deg)`;
        }
        scrollFrame = null;
      });
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("scroll", handleScroll);
      if (mouseFrame) cancelAnimationFrame(mouseFrame);
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
    };
  }, [isCoarsePointer]);

  return { spotlightRef, floorRef, isCoarsePointer };
}
